import type {
  BatchTransitionFieldName,
  BatchTransitionFormValues,
  UseBatchTransitionFormAlertProps,
  UseBatchTransitionFormAlertResult,
} from "./batch-transition-form.type";
import type { TransitionableStateValue } from "@/entities/state";
import type { StateEditFieldProps } from "@/modules/state-edit-field";
import type { FormProps } from "antd";

import { Form } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useBatchTransition } from "@/hooks/queries";
import { useBatchTransitionRowContext } from "@/modules/batch-transition/batch-transition.hook";

const fieldName: BatchTransitionFieldName = "state";

export const useBatchTransitionForm = (state: TransitionableStateValue) => {
  const {
    type,
    rowItems,
    isFinished,
    isSuccess,
    onStateValueChange,
    onMutationStatusChange,
  } = useBatchTransitionRowContext(state);

  const [form] = Form.useForm<BatchTransitionFormValues>();
  const { mutate, error, status, isPending } = useBatchTransition(`${type}s`, {
    invalidateTiming: "onSettled",
  });
  const alertData = useBatchTransitionFormAlert({ state, form, status, error });

  const initialValues: BatchTransitionFormValues = { [fieldName]: state };
  const model = rowItems[0];

  // Do not show row alert message, if all queries are successfull
  const alert = isFinished && !isSuccess ? alertData : undefined;

  useEffect(() => {
    onMutationStatusChange(state, status);
  }, [status]);

  const onSelectChange = (newState: TransitionableStateValue) => {
    onStateValueChange(state, { value: newState, form, status });
  };

  const onFormSubmit = (values: BatchTransitionFormValues) => {
    const newStateValue = values[fieldName];
    const ids = rowItems.map((item) => item.id);
    mutate({ to: newStateValue, ids });
  };

  const formProps: FormProps = {
    form,
    initialValues,
    onFinish: onFormSubmit,
  };

  const fieldProps: StateEditFieldProps = {
    name: fieldName,
    type,
    model,
    onChange: onSelectChange,
    loading: isPending,
    disabled: isFinished,
  };

  return {
    formProps,
    fieldProps,
    alert,
  };
};

const useBatchTransitionFormAlert = ({
  state: initialStateValue,
  form,
  status,
  error,
}: UseBatchTransitionFormAlertProps): UseBatchTransitionFormAlertResult => {
  const { t } = useTranslation();
  const { type, referentialStates } =
    useBatchTransitionRowContext(initialStateValue);
  const newStateValue = Form.useWatch(fieldName, form);

  const initialState = referentialStates.find(
    (s) => s.value === initialStateValue
  )?.name;

  if (status === "success") {
    const newState = referentialStates.find(
      (s) => s.value === newStateValue
    )?.name;

    return {
      type: "success",
      title: t(`${type}:batchTransition.formSuccess`, {
        initialState,
        newState,
      }),
    };
  }

  if (status === "error") {
    let title = t(`${type}:batchTransition.formErrorGeneric`, {
      initialState,
    });

    const errorsDetails = error?.response?.data?.errors ?? {};
    const items = Object.keys(errorsDetails).join(", ");

    if (items.length) {
      title += ` ${t(`${type}:batchTransition.formErrorItems`, { items })}`;
    }

    return { type: "error", title };
  }

  return undefined;
};
